import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2, Plus, Pencil } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'
import { DatabaseConnectionDialog } from '@/components/projects/connectors/DatabaseConnectionDialog'
import { databaseConnectorLabel, parseDatabaseMetadata } from '@/components/projects/connectors/databaseConnectorMetadata'
import { useConnectors, useContextSources, useTestPlans } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import type { Connector } from '@/types'
import { RepoBranchMapField } from '@/components/projects/workflows/shared/RepoBranchMapField'
import { RepoFilePickerField } from '@/components/projects/workflows/shared/RepoFilePickerField'
import { validateRepoFilePickerValue } from '@/components/projects/workflows/shared/repoFilePickerUtils'
import { dedupeRepositoryContextSources } from '@/components/projects/workflows/shared/contextSourceDedup'
import { BaseBranchHint } from '@/components/projects/workflows/shared/BaseBranchHint'
import { buildTextFieldRules } from '@/components/projects/workflows/shared/triggerFieldRules'
import { BranchNameHint } from '@/components/projects/workflows/shared/BranchNameHint'
import type { TriggerInputField } from '@/types/workflowDefinition'

type FormValues = Record<string, string>

export function formatLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim()
}

export function inputTypeFor(field: TriggerInputField): string {
  if (field.type === 'secret') return 'password'
  if (field.type === 'number' || field.type === 'integer') return 'number'
  return 'text'
}

export function isFieldVisible(field: TriggerInputField, formValues: Record<string, unknown>): boolean {
  if (!field.visibleIf) return true
  const currentValue = String(formValues[field.visibleIf.field] ?? '')
  if (field.visibleIf.anyOf) return field.visibleIf.anyOf.includes(currentValue)
  if (field.visibleIf.noneOf) return !field.visibleIf.noneOf.includes(currentValue)
  return true
}

interface TriggerFieldProps {
  projectId: string
  field: TriggerInputField
  register: ReturnType<typeof useForm<FormValues>>['register']
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
}

export function TriggerField({ projectId, field, register, errors, watch, setValue }: Readonly<TriggerFieldProps>) {
  if (!isFieldVisible(field, watch() as Record<string, unknown>)) return null
  if (field.hidden) {
    return <input type="hidden" {...register(field.name)} />
  }

  const error = errors[field.name]
  const id = `trigger-${field.name}`
  const label = field.label ?? formatLabel(field.name)
  const rules = field.required ? { required: `${label} is required` } : undefined

  if (field.type === 'boolean') {
    const checked = watch(field.name) === 'true'
    return (
      <div>
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            id={id}
            data-testid={`trigger-input-${field.name}`}
            checked={checked}
            onCheckedChange={(c) => setValue(field.name, c === true ? 'true' : 'false')}
          />
          <span>
            {label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </span>
          {field.description && (
            <span className="text-muted-foreground">— {field.description}</span>
          )}
        </label>
      </div>
    )
  }

  if (field.type === 'context-picker') {
    return (
      <ContextPickerField
        projectId={projectId}
        field={field}
        register={register}
        watch={watch}
        setValue={setValue}
        error={error?.message as string | undefined}
      />
    )
  }

  if (field.type === 'connector-picker') {
    if (field.connectorType?.toLowerCase() === 'relationaldatabase') {
      return (
        <DatabaseConnectorPickerField
          projectId={projectId}
          field={field}
          register={register}
          watch={watch}
          setValue={setValue}
          error={error?.message as string | undefined}
        />
      )
    }
    return (
      <ConnectorPickerField
        projectId={projectId}
        field={field}
        register={register}
        watch={watch}
        setValue={setValue}
        error={error?.message as string | undefined}
      />
    )
  }

  if (field.type === 'testsuite-picker') {
    return (
      <TestSuitePickerField
        projectId={projectId}
        field={field}
        register={register}
        watch={watch}
        setValue={setValue}
        error={error?.message as string | undefined}
      />
    )
  }

  if (field.type === 'repo-branch-map') {
    const dependencyFieldName = field.dependsOn ?? ''
    const rawSelected = dependencyFieldName ? (watch(dependencyFieldName) ?? '') : ''
    const selectedIds = (() => {
      if (typeof rawSelected === 'string' && rawSelected.startsWith('[')) {
        try {
          return JSON.parse(rawSelected) as string[]
        } catch {
          return []
        }
      }
      return rawSelected ? [rawSelected as string] : []
    })()
    const current = (watch(field.name) as string) ?? ''
    return (
      <>
        <input type="hidden" {...register(field.name)} />
        <RepoBranchMapField
          projectId={projectId}
          field={field}
          value={current}
          selectedContextSourceIds={selectedIds}
          onChange={(v) => setValue(field.name, v, { shouldValidate: true })}
        />
      </>
    )
  }

  if (field.type === 'repo-file-picker') {
    const current = (watch(field.name) as string) ?? ''
    return (
      <>
        <input type="hidden" {...register(field.name, {
          ...(field.required ? { required: `${label} is required` } : {}),
          validate: (v) => validateRepoFilePickerValue((v as string) ?? '', field),
        })} />
        <RepoFilePickerField
          projectId={projectId}
          field={field}
          value={current}
          onChange={(v) => setValue(field.name, v, { shouldValidate: true })}
          error={error?.message as string | undefined}
        />
      </>
    )
  }

  if (field.type === 'file-upload') {
    return (
      <FileUploadField
        field={field}
        register={register}
        setValue={setValue}
        error={error?.message as string | undefined}
      />
    )
  }

  if (field.options && field.options.length > 0) {
    const selectedValue = watch(field.name) ?? ''
    return (
      <div>
        <Label htmlFor={id} className="text-xs font-medium mb-1 block">
          {label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Select
          value={selectedValue}
          onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}
        >
          <SelectTrigger id={id} data-testid={`trigger-input-${field.name}`} className="text-sm">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" {...register(field.name, rules)} />
        {error && <p className="text-xs text-red-500 mt-1" role="alert">{error.message as string}</p>}
      </div>
    )
  }

  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        id={id}
        data-testid={`trigger-input-${field.name}`}
        type={inputTypeFor(field)}
        placeholder={field.default ? `Default: ${field.default}` : undefined}
        className="text-sm"
        {...register(field.name, buildTextFieldRules(field, label))}
      />
      {error ? (
        <p className="text-xs text-red-500 mt-1" role="alert">{error.message as string}</p>
      ) : field.description ? (
        <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
      ) : null}
      {field.baseBranchMapField && field.baseBranchRepoField && (
        <BaseBranchHint
          projectId={projectId}
          mapValue={(watch(field.baseBranchMapField) as string) ?? ''}
          repoId={(watch(field.baseBranchRepoField) as string) ?? ''}
        />
      )}
      {field.branchNameInfo && <BranchNameHint />}
    </div>
  )
}

interface PickerItem {
  id: string
  label: string
}

function ContextPickerField({
  projectId,
  field,
  register,
  watch,
  setValue,
  error,
}: {
  projectId: string
  field: TriggerInputField
  register: ReturnType<typeof useForm<FormValues>>['register']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  error?: string
}) {
  const { data: contextData, isLoading } = useContextSources(projectId, { maxResultCount: 1000 })

  const items: PickerItem[] = useMemo(() => {
    const filtered = (contextData?.items ?? []).filter(
      (item) => !field.contextType || item.kind?.toLowerCase() === field.contextType?.toLowerCase(),
    )
    return dedupeRepositoryContextSources(filtered).map((item) => ({
      id: item.id,
      label: item.kind ? `${item.name} (${item.kind})` : item.name,
    }))
  }, [contextData?.items, field.contextType])

  const label = formatLabel(field.name)
  const id = `trigger-${field.name}`
  const rules = field.required ? { required: `${label} is required` } : undefined

  if (field.multiSelect) {
    const selected = (() => {
      const raw = watch(field.name) ?? ''
      if (typeof raw === 'string' && raw.startsWith('[')) {
        try {
          return JSON.parse(raw) as string[]
        } catch {
          return []
        }
      }
      return raw ? [raw] : []
    })()

    return (
      <div>
        <Label htmlFor={id} className="text-xs font-medium mb-1 block">
          {label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {isLoading ? (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading context sources...
          </div>
        ) : (
          <MultiSelect
            options={items.map((i) => ({ value: i.id, label: i.label }))}
            value={selected}
            onChange={(newValue) => setValue(field.name, JSON.stringify(newValue), { shouldValidate: true })}
            placeholder="Select context sources..."
            displayMode="badges"
          />
        )}
        <input type="hidden" {...register(field.name, rules)} />
        {error ? (
          <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>
        ) : field.description ? (
          <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
        ) : null}
      </div>
    )
  }

  const selectedValue = watch(field.name) ?? ''
  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {isLoading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading context sources...
        </div>
      ) : (
        <Select value={selectedValue} onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}>
          <SelectTrigger id={id} data-testid={`trigger-input-${field.name}`} className="text-sm">
            <SelectValue placeholder="Select context source..." />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>
            ))}
            {items.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">No context sources available</div>}
          </SelectContent>
        </Select>
      )}
      <input type="hidden" {...register(field.name, rules)} />
      {error ? (
        <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>
      ) : field.description ? (
        <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
      ) : null}
    </div>
  )
}

function ConnectorPickerField({
  projectId,
  field,
  register,
  watch,
  setValue,
  error,
}: {
  projectId: string
  field: TriggerInputField
  register: ReturnType<typeof useForm<FormValues>>['register']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  error?: string
}) {
  const { data: connectors = [], isLoading } = useConnectors(projectId)
  const items: PickerItem[] = connectors
    .filter((c) => field.connectorType
      ? c.type.toLowerCase() === field.connectorType.toLowerCase()
      : c.category !== 'Database' && c.category !== 'CodeQuality' && c.category !== 'AiProvider')
    .map((c) => ({ id: c.id, label: c.type }))

  const label = formatLabel(field.name)
  const id = `trigger-${field.name}`
  const rules = field.required ? { required: `${label} is required` } : undefined
  const selectedValue = watch(field.name) ?? ''

  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {isLoading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading connectors...
        </div>
      ) : (
        <Select value={selectedValue} onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}>
          <SelectTrigger id={id} data-testid={`trigger-input-${field.name}`} className="text-sm">
            <SelectValue placeholder="Select connector..." />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>
            ))}
            {items.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">No connectors available</div>}
          </SelectContent>
        </Select>
      )}
      <input type="hidden" {...register(field.name, rules)} />
      {error ? (
        <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>
      ) : field.description ? (
        <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
      ) : null}
    </div>
  )
}

function DatabaseConnectorPickerField({
  projectId,
  field,
  register,
  watch,
  setValue,
  error,
}: {
  projectId: string
  field: TriggerInputField
  register: ReturnType<typeof useForm<FormValues>>['register']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  error?: string
}) {
  const { data: connectors = [], isLoading } = useConnectors(projectId)
  const { hasPermission } = useAuth(projectId)
  const canManage = hasPermission(Permissions.Project.Connectors.Manage, projectId)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Connector | null>(null)

  const databaseConnectors = connectors.filter(
    (c) => c.type.toLowerCase() === field.connectorType?.toLowerCase(),
  )

  const label = formatLabel(field.name)
  const id = `trigger-${field.name}`
  const rules = field.required ? { required: `${label} is required` } : undefined
  const selectedValue = watch(field.name) ?? ''
  const selectedConnector = databaseConnectors.find((c) => c.id === selectedValue) ?? null

  useEffect(() => {
    setValue(`${field.name}__dbType`, selectedConnector ? (parseDatabaseMetadata(selectedConnector.providerMetadata)?.dbType ?? '') : '')
  }, [selectedConnector, field.name, setValue])

  const openAdd = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = () => {
    if (!selectedConnector) return
    setEditing(selectedConnector)
    setDialogOpen(true)
  }

  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {isLoading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading connections...
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Select value={selectedValue} onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}>
            <SelectTrigger id={id} data-testid={`trigger-input-${field.name}`} className="text-sm flex-1">
              <SelectValue placeholder="Select database connection..." />
            </SelectTrigger>
            <SelectContent>
              {databaseConnectors.map((c) => (
                <SelectItem key={c.id} value={c.id}>{databaseConnectorLabel(c)}</SelectItem>
              ))}
              {databaseConnectors.length === 0 && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">No database connections yet</div>
              )}
            </SelectContent>
          </Select>
          {canManage && selectedConnector && (
            <Button type="button" variant="outline" size="icon" onClick={openEdit} aria-label="Edit connection" title="Edit connection" data-testid={`trigger-input-${field.name}-edit`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {canManage && (
            <Button type="button" variant="outline" size="icon" onClick={openAdd} aria-label="Add connection" title="Add connection" data-testid={`trigger-input-${field.name}-add`}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
      <input type="hidden" {...register(field.name, rules)} />
      {error ? (
        <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>
      ) : field.description ? (
        <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
      ) : null}
      {canManage && (
        <DatabaseConnectionDialog
          projectId={projectId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          existing={editing}
          onSaved={(connectorId) => setValue(field.name, connectorId, { shouldValidate: true })}
        />
      )}
    </div>
  )
}

function TestSuitePickerField({
  projectId,
  field,
  register,
  watch,
  setValue,
  error,
}: {
  projectId: string
  field: TriggerInputField
  register: ReturnType<typeof useForm<FormValues>>['register']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  error?: string
}) {
  const { data: plansData, isLoading } = useTestPlans(projectId, { maxResultCount: 1000, skipCount: 0 })

  const items: PickerItem[] = useMemo(() => {
    const plans = plansData?.items ?? []
    return plans.flatMap(plan =>
      (plan.testSuites ?? []).map(suite => ({
        id: suite.id,
        label: `${plan.title} / ${suite.title}`,
      })),
    )
  }, [plansData])

  const label = field.label ?? 'Test suite'
  const id = `trigger-${field.name}`
  const rules = field.required ? { required: `${label} is required` } : undefined
  const selectedValue = watch(field.name) ?? ''

  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {isLoading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading test suites...
        </div>
      ) : (
        <Select value={selectedValue} onValueChange={(v) => setValue(field.name, v, { shouldValidate: true })}>
          <SelectTrigger id={id} data-testid={`trigger-input-${field.name}`} className="text-sm">
            <SelectValue placeholder="Select test suite..." />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>
            ))}
            {items.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">No test suites — create a test plan and suite first</div>}
          </SelectContent>
        </Select>
      )}
      <input type="hidden" {...register(field.name, rules)} />
      {error ? (
        <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>
      ) : field.description ? (
        <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
      ) : null}
    </div>
  )
}

function FileUploadField({
  field,
  register,
  setValue,
  error,
}: {
  field: TriggerInputField
  register: ReturnType<typeof useForm<FormValues>>['register']
  setValue: ReturnType<typeof useForm<FormValues>>['setValue']
  error?: string
}) {
  const [fileName, setFileName] = useState<string | null>(null)
  const [sizeError, setSizeError] = useState<string | null>(null)

  const id = `trigger-${field.name}`
  const label = formatLabel(field.name)
  const rules = field.required ? { required: `${label} is required` } : undefined

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (field.maxFileSizeBytes && file.size > field.maxFileSizeBytes) {
      setSizeError(`File exceeds maximum size of ${formatBytes(field.maxFileSizeBytes)}`)
      return
    }

    setSizeError(null)
    setFileName(file.name)

    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      setValue(field.name, base64, { shouldValidate: true })
    }
    reader.readAsDataURL(file)
  }

  const displayError = sizeError ?? error

  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <input
        id={id}
        type="file"
        accept={field.accept ?? undefined}
        onChange={handleChange}
        data-testid={`trigger-input-${field.name}`}
        className="text-sm w-full file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-muted file:text-foreground hover:file:bg-muted/80 cursor-pointer"
      />
      <input type="hidden" {...register(field.name, rules)} />
      {fileName && !displayError && (
        <p className="text-xs text-muted-foreground mt-0.5">Selected: {fileName}</p>
      )}
      {field.maxFileSizeBytes && !displayError && (
        <p className="text-xs text-muted-foreground mt-0.5">Max size: {formatBytes(field.maxFileSizeBytes)}</p>
      )}
      {displayError ? (
        <p className="text-xs text-red-500 mt-1" role="alert">{displayError}</p>
      ) : field.description ? (
        <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
      ) : null}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
